import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  FormControl,
  FormLabel,
  Input,
  Select,
  Grid,
  GridItem,
  Checkbox,
} from '@chakra-ui/react';

import DateField from 'components/fields/DateField';
import SelectSearch from 'components/fields/SelectSearch';
import {
  parseDateString,
  formatDateToString,
} from 'components/fields/auto/resolvers/resolverUtils';
import type { Floater } from 'api/quote-marketplace';
import { ValidatedField } from '.';
import { useValidationSchema } from '../hooks';

export interface FloaterModalProps {
  isOpen: boolean;
  onClose: () => void;
  floater: Floater;
  onChange: (floater: Floater) => void;
  onSave: () => void;
  itemTypes: { value: string; label: string }[];
  coverageRequiredOptions: { value: string; label: string }[];
  itemExhibitedOptions: { value: string; label: string }[];
  title?: string;
}

const FloaterModal = ({
  isOpen,
  onClose,
  floater,
  onChange,
  onSave,
  itemTypes,
  coverageRequiredOptions,
  itemExhibitedOptions,
  title = 'Floater',
}: FloaterModalProps) => {
  const { isFieldRequired } = useValidationSchema();
  const floaterEntity = floater as unknown as Record<string, unknown>;

  const isItemTypeRequired = isFieldRequired(
    'location/floater',
    'item_type',
    floaterEntity,
  );
  const isItemTypeInvalid = isItemTypeRequired && !floater.item_type;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="2xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          {floater.id ? `Edit ${title}` : `Add ${title}`}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Grid templateColumns="repeat(2, 1fr)" gap={4}>
            <GridItem colSpan={2}>
              <FormControl
                isRequired={isItemTypeRequired}
                isInvalid={isItemTypeInvalid}
              >
                <FormLabel fontSize="sm">Item Type</FormLabel>
                <SelectSearch
                  options={itemTypes}
                  value={floater.item_type || ''}
                  onChange={(value) =>
                    onChange({
                      ...floater,
                      item_type: value ? String(value) : '',
                    })
                  }
                  placeholder="Search item types..."
                  isInvalid={isItemTypeInvalid}
                />
              </FormControl>
            </GridItem>
            <GridItem colSpan={2}>
              <FormControl>
                <FormLabel fontSize="sm">Description</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={floater.item_description}
                  onChange={(e) =>
                    onChange({
                      ...floater,
                      item_description: e.target.value,
                    })
                  }
                  placeholder="Item description"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <ValidatedField
                itemPath="location/floater"
                fieldPath="item_value_cad"
                label="Value (CAD)"
                entity={floaterEntity}
              >
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  value={floater.item_value_cad ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...floater,
                      item_value_cad: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                />
              </ValidatedField>
            </GridItem>
            <GridItem>
              <ValidatedField
                itemPath="location/floater"
                fieldPath="deductible_cad"
                label="Deductible (CAD)"
                entity={floaterEntity}
              >
                <Input
                  variant="main"
                  size="sm"
                  type="number"
                  value={floater.deductible_cad ?? ''}
                  onChange={(e) =>
                    onChange({
                      ...floater,
                      deductible_cad: e.target.value
                        ? Number(e.target.value)
                        : null,
                    })
                  }
                />
              </ValidatedField>
            </GridItem>
            <GridItem>
              <DateField
                label="Purchase Date"
                date={parseDateString(floater.purchase_date || '')}
                onChange={(date: Date | null) =>
                  onChange({
                    ...floater,
                    purchase_date: formatDateToString(date),
                  })
                }
                size="sm"
              />
            </GridItem>
            <GridItem>
              <DateField
                label="Valuation Date"
                date={parseDateString(floater.valuation_date || '')}
                onChange={(date: Date | null) =>
                  onChange({
                    ...floater,
                    valuation_date: formatDateToString(date),
                  })
                }
                size="sm"
              />
            </GridItem>
            <GridItem colSpan={2}>
              <FormControl>
                <FormLabel fontSize="sm">Serial/ID Number</FormLabel>
                <Input
                  variant="main"
                  size="sm"
                  value={floater.serial_identification_number}
                  onChange={(e) =>
                    onChange({
                      ...floater,
                      serial_identification_number: e.target.value,
                    })
                  }
                  placeholder="Serial or identification number"
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <ValidatedField
                itemPath="location/floater"
                fieldPath="coverage_required"
                label="Coverage Required"
                entity={floaterEntity}
              >
                <Select
                  variant="main"
                  size="sm"
                  value={floater.coverage_required || ''}
                  onChange={(e) =>
                    onChange({
                      ...floater,
                      coverage_required: e.target.value,
                    })
                  }
                >
                  {coverageRequiredOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </ValidatedField>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel fontSize="sm">Item Exhibited</FormLabel>
                <Select
                  variant="main"
                  size="sm"
                  value={floater.item_exhibited || ''}
                  onChange={(e) =>
                    onChange({
                      ...floater,
                      item_exhibited: e.target.value,
                    })
                  }
                >
                  {itemExhibitedOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </GridItem>
            <GridItem>
              <Checkbox
                isChecked={floater.item_used_for_business}
                onChange={(e) =>
                  onChange({
                    ...floater,
                    item_used_for_business: e.target.checked,
                  })
                }
                size="sm"
                mt={6}
              >
                Used for Business
              </Checkbox>
            </GridItem>
          </Grid>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>
            Cancel
          </Button>
          <Button colorScheme="yellow" onClick={onSave}>
            Save
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default FloaterModal;
